/**
 * OCP-236 — OAuth consent screen (admin SPA route `/oauth/authorize`).
 *
 * An AI client (Claude Desktop, Cursor, VS Code Copilot) redirects the
 * operator's browser here as `/oauth/authorize?client_id=...&...` (the
 * `authorization_endpoint` from the discovery metadata). This page:
 *   1. Parses + validates the RFC 6749 + PKCE query params.
 *   2. If the operator isn't logged in → bounces to /login (router
 *      state carries the return path so login lands back here).
 *   3. Looks up client_name via GET /oauth/clients/:id/public.
 *   4. Renders "Authorize {client_name}?" with Authorize / Deny.
 *   5. POSTs the decision → window.location = returned redirect_to.
 *
 * OCP-783 - the operator chooses Read only (default) or Read and write. The
 * text comes from GET /oauth/consent-info: the number of read and write
 * tools of THIS role, the destructive ones by name, what the role does not
 * carry, and how long the client stays connected through its refresh token.
 * Read only is enforced by the API on every mutation (403 read_only_grant).
 *
 * The PKCE code_challenge is generated by the AI client, not us — we
 * pass it through. `state` round-trips so the client detects callback
 * forgery.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../lib/auth-context';
import { getOAuthConsentInfo, getPublicOAuthClient, postOAuthDecision, type OAuthConsentInfo, type PublicOAuthClient } from '../lib/oauth';

interface AuthorizeParams {
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  code_challenge: string;
  code_challenge_method: 'S256';
}

function parseParams(sp: URLSearchParams): { params: AuthorizeParams | null; error: string | null } {
  const responseType = sp.get('response_type');
  if (responseType !== 'code') {
    return { params: null, error: `response_type must be "code" (got "${responseType ?? ''}")` };
  }
  const required = ['client_id', 'redirect_uri', 'scope', 'state', 'code_challenge', 'code_challenge_method'] as const;
  const missing = required.filter((k) => !sp.get(k));
  if (missing.length > 0) {
    return { params: null, error: `Missing required parameter${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}` };
  }
  if (sp.get('code_challenge_method') !== 'S256') {
    return { params: null, error: `code_challenge_method must be "S256" (got "${sp.get('code_challenge_method') ?? ''}")` };
  }
  return {
    params: {
      client_id: sp.get('client_id')!,
      redirect_uri: sp.get('redirect_uri')!,
      scope: sp.get('scope')!,
      state: sp.get('state')!,
      code_challenge: sp.get('code_challenge')!,
      code_challenge_method: 'S256',
    },
    error: null,
  };
}

export function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { params, error: paramError } = parseParams(searchParams);

  const [client, setClient] = useState<PublicOAuthClient | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [info, setInfo] = useState<OAuthConsentInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [access, setAccess] = useState<'read' | 'write'>('read');

  // Bounce to /login (carrying the full return path) once auth resolves
  // to unauthenticated. Done in an effect so we never flash the consent
  // card before redirecting.
  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
    }
  }, [status, navigate, location.pathname, location.search]);

  // Fetch the client_name once authenticated + params valid.
  useEffect(() => {
    if (status !== 'authenticated' || !params) return;
    let cancelled = false;
    getPublicOAuthClient(params.client_id)
      .then((c) => { if (!cancelled) setClient(c); })
      .catch(() => { if (!cancelled) setClientError(params.client_id); });
    return () => { cancelled = true; };
  }, [status, params?.client_id]);

  // OCP-783 - what the grant carries, generated server-side for this operator.
  useEffect(() => {
    if (status !== 'authenticated' || !params) return;
    let cancelled = false;
    getOAuthConsentInfo(params.scope)
      .then((i) => { if (!cancelled) setInfo(i); })
      .catch((err) => { if (!cancelled) setInfoError(err instanceof Error ? err.message : 'Could not load what this grant carries'); });
    return () => { cancelled = true; };
  }, [status, params?.scope]);

  const decide = async (allow: boolean) => {
    if (!params) return;
    setSubmitError(null);
    setSubmitting(allow ? 'allow' : 'deny');
    try {
      const res = await postOAuthDecision({ ...params, allow, access: info?.read_only_requested ? 'read' : access });
      window.location.href = res.redirect_to;
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to record decision');
      setSubmitting(null);
    }
  };

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (paramError) {
    return <ConsentError title="Invalid OAuth request" detail={paramError} />;
  }

  if (clientError) {
    return (
      <ConsentError
        title="Unknown OAuth client"
        detail={`No client registered with client_id "${clientError}". The AI client may need to re-register.`}
      />
    );
  }

  if (infoError) {
    return <ConsentError title="Cannot show what this grant carries" detail={infoError} />;
  }

  if (!client || !info) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const redirectAllowed = client.redirect_uris.includes(params!.redirect_uri);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-5 flex items-start gap-3">
          <div className="shrink-0 rounded-lg bg-brand-50 dark:bg-brand-950/40 p-2.5">
            <LockIcon />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Authorize {client.client_name}?
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Signed in as{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">{user?.email}</span>
            </p>
          </div>
        </div>

        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
          <strong>{client.client_name}</strong> wants to connect to orboto Control on your behalf.
        </p>

        <fieldset className="mb-5 space-y-2" data-testid="consent-access">
          <legend className="mb-2 text-sm font-medium text-gray-900 dark:text-white">Access</legend>
          <label className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
            <input
              type="radio"
              name="access"
              value="read"
              checked={access === 'read' || info.read_only_requested}
              onChange={() => setAccess('read')}
              className="mt-0.5"
              data-testid="access-read"
            />
            <span>
              <span className="block font-medium text-gray-900 dark:text-white">Read only</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {info.read.tool_count} read tools: every admin record they expose - customers and their accounts,
                tenants, billing, servers, jobs, settings and the audit log. Any change is refused by the API.
              </span>
            </span>
          </label>
          <label className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${info.read_only_requested ? 'border-gray-100 dark:border-gray-800 opacity-50' : 'border-gray-200 dark:border-gray-700'}`}>
            <input
              type="radio"
              name="access"
              value="write"
              checked={access === 'write' && !info.read_only_requested}
              disabled={info.read_only_requested}
              onChange={() => setAccess('write')}
              className="mt-0.5"
              data-testid="access-write"
            />
            <span>
              <span className="block font-medium text-gray-900 dark:text-white">Read and write as {info.role}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400" data-testid="consent-write-detail">
                Everything above plus {info.write.tool_count} write tools of your role
                {info.write.account_administration ? ', account administration included (team, roles, bot grants, MFA resets, OAuth clients)' : ''}.
                {info.write.not_included.length > 0 ? ` Not included: ${info.write.not_included.join('; ')}.` : ''}
                {info.read_only_requested ? ' The client asked for read-only access.' : ''}
              </span>
            </span>
          </label>
        </fieldset>

        {access === 'write' && !info.read_only_requested && info.write.destructive.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300" data-testid="consent-destructive">
            <div className="mb-1 flex items-center gap-1.5 font-medium">
              <AlertIcon />
              Includes {info.write.destructive.length} destructive operations
            </div>
            <ul className="list-disc pl-5">
              {info.write.destructive.map((d) => (
                <li key={d.name}>{d.title}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-5 space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <ConsentRow
            title="Stays connected without you"
            detail={`The client renews its ${info.access_token_ttl_minutes}-minute access with a refresh token on its own. It keeps access until you revoke it, or after ${info.refresh_token_idle_days} days without use.`}
          />
          <ConsentRow
            title="Never more than your account"
            detail={`The client acts as ${user?.email ?? 'your account'} with the role ${info.role}. If your role, password or second factor changes, its access follows or ends.`}
          />
        </div>

        {!redirectAllowed && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
            <AlertIcon />
            <span>
              The redirect URI in this request is not on this client&apos;s whitelist. The
              authorization will fail even if you click Authorize. Cancel and ask the AI client
              developer to re-register.
            </span>
          </div>
        )}

        {submitError && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            <AlertIcon />
            <span>{submitError}</span>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => decide(false)}
            disabled={submitting !== null}
            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting === 'deny' ? 'Cancelling…' : 'Deny'}
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            disabled={submitting !== null}
            className="flex-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting === 'allow' ? 'Authorizing…' : 'Authorize'}
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Tokens issued here can be revoked at any time under Admin → OAuth clients.
        </p>
      </div>
    </div>
  );
}

function ConsentRow({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <CheckIcon />
      <div>
        <div className="font-medium text-gray-900 dark:text-white">{title}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{detail}</div>
      </div>
    </div>
  );
}

function ConsentError({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-3 flex items-start gap-3">
          <AlertIcon />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300">{detail}</p>
      </div>
    </div>
  );
}

// ---- inline icons (admin SPA has no icon lib) ----

function LockIcon() {
  return (
    <svg className="h-5 w-5 text-brand-500 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-current" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    </svg>
  );
}

export default OAuthConsentPage;
